import React from 'react';

export interface StepBase {
  /**
   * The value the next stepper press steps from, and the value the steppers'
   * limits are judged against.
   */
  readonly latest: number;
  /** Records a value a press emitted and re-renders so the steppers' disabled state follows it. */
  advance(next: number): void;
}

/**
 * The value a stepper press should step from.
 *
 * The picker is controlled, and its owner may only hand the new value back
 * after an async round trip (a json-render state binding re-renders the whole
 * spec). Stepping from the `value` prop would make a second fast tap repeat the
 * first one's result, so presses step from the last value they emitted. Any
 * change of the prop wins and resets the base: the owner's value is the truth
 * as soon as it moves.
 *
 * The base lives in a ref so two taps inside one frame both see the first
 * one's result; the re-render after each press keeps the disabled state of the
 * steppers in step with it, so a decrement enabled by a pending increment can
 * be pressed before the owner catches up.
 */
export function useStepBase(value: number): StepBase {
  const ref = React.useRef({ prop: value, latest: value });
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);
  if (ref.current.prop !== value) {
    ref.current = { prop: value, latest: value };
  }
  const entry = ref.current;
  return {
    get latest() {
      return entry.latest;
    },
    advance(next: number) {
      entry.latest = next;
      rerender();
    },
  };
}
